import { applyLazyBg, pickLazyBg } from './lazy-bg';

const AUTOPLAY_INTERVAL_MS = 5000;
const SWIPE_THRESHOLD_PX = 50;
const SCROLL_INTO_VIEW_PADDING_PX = 12;

function ensureTabVisible(btn: HTMLElement, navWrap: HTMLElement): void {
    const btnRect = btn.getBoundingClientRect();
    const wrapRect = navWrap.getBoundingClientRect();
    if (btnRect.left < wrapRect.left + SCROLL_INTO_VIEW_PADDING_PX) {
        navWrap.scrollBy({
            left: btnRect.left - wrapRect.left - SCROLL_INTO_VIEW_PADDING_PX,
            behavior: 'smooth',
        });
    } else if (btnRect.right > wrapRect.right - SCROLL_INTO_VIEW_PADDING_PX) {
        navWrap.scrollBy({
            left: btnRect.right - wrapRect.right + SCROLL_INTO_VIEW_PADDING_PX,
            behavior: 'smooth',
        });
    }
}

export const initTabs = (): void => {
    const tabGroups = document.querySelectorAll<HTMLElement>('.tabs-nav');

    tabGroups.forEach((nav: HTMLElement) => {
        const groupName = nav.getAttribute('data-tab-group');
        const contentContainer = document.querySelector<HTMLElement>(`.tabs-content[data-tab-group="${groupName}"]`);

        if (!contentContainer) return;

        const navWrap = nav.closest<HTMLElement>('.tabs-nav-wrp') || nav;

        let touchStartX = 0;
        let touchEndX = 0;
        const buttons = Array.from(nav.querySelectorAll<HTMLElement>('.tab-btn'));
        let autoPlayInterval: number | undefined;
        // Autoplay is gated on visibility — startAutoPlay is a no-op when the
        // tabs aren't on screen, so the IntersectionObserver delays the very
        // first tick until the user actually scrolls them into view.
        let isInView = false;

        const activateTab = (btn: HTMLElement) => {
            nav.querySelectorAll<HTMLElement>('.tab-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            ensureTabVisible(btn, navWrap);

            const targetId = btn.getAttribute('data-tab-target');
            if (!targetId) return;

            contentContainer.querySelectorAll<HTMLElement>('.tab-pane').forEach(pane => {
                pane.classList.remove('active');
            });

            const targetPane = document.getElementById(targetId);
            if (targetPane) {
                targetPane.classList.add('active');

                targetPane.querySelectorAll<HTMLElement>('.lazy-bg[data-bg]').forEach(applyLazyBg);

                const currentIndex = buttons.findIndex(b => b === btn);
                if (currentIndex > -1) {
                    const nextTargetId = buttons[(currentIndex + 1) % buttons.length].getAttribute('data-tab-target');
                    if (nextTargetId) {
                        const nextPane = document.getElementById(nextTargetId);
                        if (nextPane) {
                            nextPane.querySelectorAll<HTMLElement>('.lazy-bg[data-bg]').forEach(el => {
                                if (el.style.backgroundImage) return;
                                const url = pickLazyBg(el);
                                if (url) {
                                    const img = new Image();
                                    img.src = url;
                                }
                            });
                        }
                    }
                }
            }
        };

        const startAutoPlay = () => {
            stopAutoPlay();
            if (!isInView) return;
            autoPlayInterval = window.setInterval(() => {
                const activeIndex = buttons.findIndex(btn => btn.classList.contains('active'));
                if (activeIndex > -1) {
                    const nextIndex = (activeIndex + 1) % buttons.length;
                    // Use activateTab directly instead of .click() to avoid
                    // bubbling a synthetic click to document, which would
                    // close the header dropdown menu (header.ts closes on
                    // any document click outside #main-menu).
                    activateTab(buttons[nextIndex]);
                }
            }, AUTOPLAY_INTERVAL_MS);
        };

        const stopAutoPlay = () => {
            if (autoPlayInterval) window.clearInterval(autoPlayInterval);
            autoPlayInterval = undefined;
        };

        if ('IntersectionObserver' in window) {
            const visibilityObs = new IntersectionObserver(entries => {
                for (const entry of entries) {
                    isInView = entry.isIntersecting;
                    if (isInView) startAutoPlay();
                    else stopAutoPlay();
                }
            }, { threshold: 0.2 });
            visibilityObs.observe(navWrap);
        } else {
            isInView = true;
            startAutoPlay();
        }

        nav.addEventListener('mouseenter', stopAutoPlay);
        nav.addEventListener('mouseleave', startAutoPlay);
        contentContainer.addEventListener('mouseenter', stopAutoPlay);
        contentContainer.addEventListener('mouseleave', startAutoPlay);

        contentContainer.addEventListener('touchstart', e => {
            stopAutoPlay();
            touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        contentContainer.addEventListener('touchend', e => {
            touchEndX = e.changedTouches[0].screenX;
            handleSwipe();
            startAutoPlay();
        }, { passive: true });

        const handleSwipe = () => {
            const activeIndex = buttons.findIndex(btn => btn.classList.contains('active'));
            if (activeIndex === -1) return;

            if (touchEndX < touchStartX - SWIPE_THRESHOLD_PX && activeIndex < buttons.length - 1) {
                activateTab(buttons[activeIndex + 1]);
            } else if (touchEndX > touchStartX + SWIPE_THRESHOLD_PX && activeIndex > 0) {
                activateTab(buttons[activeIndex - 1]);
            }
        };

        nav.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const btn = target.closest<HTMLElement>('.tab-btn');
            if (!btn) return;

            activateTab(btn);
        });
    });
};
